import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  Row,
  Col,
  Button,
  Spin,
  Alert,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../store";
import {
  createStockItemStart,
  updateStockItemStart,
} from "../store/slices/stockSlice";
import { getSuppliersStart } from "../store/slices/supplierSlice";
import type { StockItem } from "../types/Stock";
import medicineService, { Medicine } from "../services/medicineService";
import dayjs from "dayjs";
import { Link } from "react-router-dom";

const { Option } = Select;

interface StockFormProps {
  visible: boolean;
  onCancel: () => void;
  editData?: StockItem | null;
  onSuccess: () => void;
}

const StockForm: React.FC<StockFormProps> = ({
  visible,
  onCancel,
  editData,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { loading } = useSelector((state: RootState) => state.stock);
  const { suppliers } = useSelector((state: RootState) => state.suppliers);
  const [medicines, setMedicines] = useState<Medicine[]>([]);

  useEffect(() => {
    if (visible) {
      dispatch(getSuppliersStart({}) as any);
      medicineService.getAll({ limit: 500 }).then((res) => {
        setMedicines(res.data.data || []);
      });
      if (editData) {
        form.setFieldsValue({
          medicine: (editData as any).medicine?._id || (editData as any).medicine,
          supplier: editData.supplier?._id,
          batchNumber: editData.batchNumber,
          expiryDate: dayjs(editData.expiryDate),
          manufacturingDate: dayjs(editData.manufacturingDate),
          currentQuantity: editData.quantity.current,
          minimumQuantity: editData.quantity.minimum,
          unit: editData.quantity.unit,
          purchasePrice: editData.pricing.purchasePrice,
          sellingPrice: editData.pricing.sellingPrice,
          mrp: editData.pricing.mrp,
          rack: editData.location?.rack,
          shelf: editData.location?.shelf,
          position: editData.location?.position,
          notes: editData.notes,
        });
      } else {
        form.resetFields();
        form.setFieldsValue({ unit: "pieces", minimumQuantity: 10 });
      }
    }
  }, [visible, editData, form, dispatch]);

  const onMedicineChange = (id: string) => {
    const med = medicines.find((m) => m._id === id);
    if (!med) return;
    form.setFieldsValue({
      unit: med.unit,
      minimumQuantity: med.reorderLevel,
      // Medicine defaults are per purchase pack → convert to per stock unit
      mrp: Number(
        ((med.defaultMrp || 0) / Math.max(1, med.packSize || 1)).toFixed(4)
      ),
      sellingPrice: Number(
        (
          (med.defaultSellingPrice || med.defaultMrp || 0) /
          Math.max(1, med.packSize || 1)
        ).toFixed(4)
      ),
    });
  };

  const handleSubmit = async (values: any) => {
    try {
      const med =
        medicines.find((m) => m._id === values.medicine) ||
        (editData as any)?.medicine;

      const stockData: any = {
        medicine: values.medicine,
        medicineId: med?.code || editData?.medicineId,
        name: med?.name || editData?.name,
        genericName: med?.genericName ?? editData?.genericName,
        category: med?.category || editData?.category,
        manufacturer: med?.manufacturer || editData?.manufacturer,
        hsnCode: med?.hsnCode ?? (editData as any)?.hsnCode,
        gstRate: med?.defaultGstRate ?? (editData as any)?.gstRate,
        batchNumber: values.batchNumber,
        expiryDate: values.expiryDate.toISOString(),
        manufacturingDate: values.manufacturingDate.toISOString(),
        quantity: {
          current: values.currentQuantity,
          minimum: values.minimumQuantity,
          unit: values.unit,
        },
        pricing: {
          purchasePrice: values.purchasePrice,
          sellingPrice: values.sellingPrice,
          mrp: values.mrp,
          discount: editData?.pricing?.discount ?? 0,
        },
        location: {
          rack: values.rack,
          shelf: values.shelf,
          position: values.position,
        },
        notes: values.notes,
        isActive: true,
        alertLowStock: true,
      };

      // Only send supplier when selected (avoid empty string CastError)
      if (values.supplier) {
        stockData.supplier = values.supplier;
      } else {
        stockData.supplier = null;
      }

      if (editData) {
        dispatch(
          updateStockItemStart({ id: editData._id, data: stockData }) as any
        );
      } else {
        dispatch(createStockItemStart(stockData) as any);
      }

      onSuccess();
    } catch {
      // Form validation errors are shown by Ant Design Form
    }
  };

  return (
    <Modal
      title={editData ? "Edit Stock Batch" : "Opening / Manual Stock"}
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={900}
      destroyOnClose
    >
      {!editData && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={
            <span>
              Enter quantity in <strong>stock / sale units</strong> (e.g. tablets),
              not strips. Prefer{" "}
              <Link to="/purchases/new">Purchase Invoice</Link> for supplier
              receipts — it converts packs → stock units automatically.
            </span>
          }
        />
      )}
      <Spin spinning={loading}>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="medicine"
                label="Medicine"
                rules={[{ required: true, message: "Select medicine" }]}
              >
                <Select
                  showSearch
                  optionFilterProp="children"
                  placeholder="Select from product master"
                  onChange={onMedicineChange}
                  filterOption={(input, option) =>
                    String(option?.children ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  {medicines.map((m) => (
                    <Option key={m._id} value={m._id}>
                      {m.code} — {m.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="supplier" label="Supplier">
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  placeholder="Optional for opening stock"
                  filterOption={(input, option) =>
                    String(option?.children ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  {suppliers.map((s) => (
                    <Option key={s._id} value={s._id}>
                      {s.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="batchNumber"
                label="Batch Number"
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="manufacturingDate"
                label="Manufacturing Date"
                rules={[{ required: true }]}
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="expiryDate"
                label="Expiry Date"
                rules={[{ required: true }]}
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={6}>
              <Form.Item
                name="currentQuantity"
                label="Quantity"
                rules={[{ required: true }]}
              >
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="minimumQuantity"
                label="Min Qty"
                rules={[{ required: true }]}
              >
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="unit" label="Unit" rules={[{ required: true }]}>
                <Select showSearch>
                  {["pieces", "bottles", "boxes", "strips", "vials"].map((u) => (
                    <Option key={u} value={u}>
                      {u}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="purchasePrice"
                label="Purchase Price"
                rules={[{ required: true }]}
              >
                <InputNumber min={0} precision={2} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="sellingPrice"
                label="Selling Price"
                rules={[{ required: true }]}
              >
                <InputNumber min={0} precision={2} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="mrp" label="MRP" rules={[{ required: true }]}>
                <InputNumber min={0} precision={2} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="notes" label="Notes">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="rack" label="Rack">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="shelf" label="Shelf">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="position" label="Position">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row justify="end" gutter={8}>
            <Col>
              <Button onClick={onCancel}>Cancel</Button>
            </Col>
            <Col>
              <Button type="primary" htmlType="submit" loading={loading}>
                {editData ? "Update" : "Save Opening Stock"}
              </Button>
            </Col>
          </Row>
        </Form>
      </Spin>
    </Modal>
  );
};

export default StockForm;
